import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Shield,
    Search,
    RefreshCw,
    User,
    LogIn,
    LogOut,
    Trash2,
    Edit,
    Plus,
    Clock,
    AlertTriangle,
    Filter,
    ChevronLeft,
    ChevronRight,
    Users,
    CheckSquare,
    Briefcase,
    XCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const AuditLogPage = () => {
    const { api, user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(25);
    const [filters, setFilters] = useState({
        entity_type: '',
        action: '',
        search: ''
    });

    // Check if user is admin
    const isAdmin = user?.role === 'chef';

    const fetchLogs = useCallback(async () => {
        if (!isAdmin) return;
        
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('skip', page * pageSize);
            params.append('limit', pageSize);
            if (filters.entity_type) params.append('entity_type', filters.entity_type);
            if (filters.action) params.append('action', filters.action);
            
            const response = await api.get(`/system/audit-logs?${params.toString()}`);
            setLogs(response.data.logs || []);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
        } finally {
            setLoading(false);
        }
    }, [api, isAdmin, page, pageSize, filters.entity_type, filters.action]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Action icons and colors
    const getActionStyle = (action) => {
        switch (action) {
            case 'login':
                return { icon: LogIn, color: 'bg-green-500/10 text-green-600', label: 'Login' };
            case 'login_failed':
                return { icon: XCircle, color: 'bg-red-500/10 text-red-600', label: 'Login fehlgeschlagen' };
            case 'logout':
                return { icon: LogOut, color: 'bg-gray-500/10 text-gray-600', label: 'Logout' };
            case 'create':
                return { icon: Plus, color: 'bg-blue-500/10 text-blue-600', label: 'Erstellt' };
            case 'update':
                return { icon: Edit, color: 'bg-amber-500/10 text-amber-600', label: 'Bearbeitet' };
            case 'delete':
                return { icon: Trash2, color: 'bg-red-500/10 text-red-600', label: 'Gelöscht' };
            default:
                return { icon: Clock, color: 'bg-gray-500/10 text-gray-600', label: action };
        }
    };

    // Entity type icons
    const getEntityIcon = (entityType) => {
        switch (entityType) {
            case 'user':
                return User;
            case 'auth':
                return Shield;
            case 'task':
                return CheckSquare;
            case 'customer':
                return Briefcase;
            case 'time_entry':
                return Clock;
            default:
                return AlertTriangle;
        }
    };

    // Entity type labels
    const getEntityLabel = (entityType) => {
        const labels = {
            'user': 'Benutzer',
            'auth': 'Authentifizierung',
            'task': 'Task',
            'customer': 'Kunde',
            'time_entry': 'Zeiterfassung',
            'project': 'Projekt',
            'vacation': 'Urlaub'
        };
        return labels[entityType] || entityType;
    };

    // Format details for display
    const formatDetails = (details) => {
        if (!details || Object.keys(details).length === 0) return '-';
        
        const items = [];
        if (details.customer_name) items.push(`Kunde: ${details.customer_name}`);
        if (details.task_title) items.push(`Task: ${details.task_title}`);
        if (details.deleted_user) items.push(`Benutzer: ${details.deleted_user}`);
        if (details.new_user) items.push(`Neu: ${details.new_user}`);
        if (details.updated_user) items.push(`Benutzer: ${details.updated_user}`);
        if (details.email) items.push(`E-Mail: ${details.email}`);
        if (details.role) items.push(`Rolle: ${details.role}`);
        if (details.reason) items.push(`Grund: ${details.reason}`);
        if (details.changes) items.push(`Änderungen: ${details.changes.join(', ')}`);
        if (details.old_duration_seconds !== undefined && details.new_duration_seconds !== undefined) {
            const oldHours = (details.old_duration_seconds / 3600).toFixed(2);
            const newHours = (details.new_duration_seconds / 3600).toFixed(2);
            items.push(`Zeit: ${oldHours}h → ${newHours}h`);
        }
        if (details.duration_seconds && !details.old_duration_seconds) {
            const hours = (details.duration_seconds / 3600).toFixed(2);
            items.push(`Dauer: ${hours}h`);
        }
        if (details.description) items.push(`Beschreibung: ${details.description}`);
        
        return items.length > 0 ? items.join(' | ') : JSON.stringify(details);
    };

    // Filter logs by search
    const filteredLogs = logs.filter(log => {
        if (!filters.search) return true;
        const searchLower = filters.search.toLowerCase();
        return (
            log.user_name?.toLowerCase().includes(searchLower) ||
            log.action?.toLowerCase().includes(searchLower) ||
            log.entity_type?.toLowerCase().includes(searchLower) ||
            JSON.stringify(log.details || {}).toLowerCase().includes(searchLower)
        );
    });

    const totalPages = Math.ceil(total / pageSize);

    if (!isAdmin) {
        return (
            <div className="flex-1 p-6 flex items-center justify-center">
                <Card className="max-w-md">
                    <CardContent className="p-8 text-center">
                        <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
                        <h2 className="text-xl font-bold mb-2">Zugriff verweigert</h2>
                        <p className="text-muted-foreground">
                            Nur Administratoren können das Audit-Log einsehen.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex-1 p-4 md:p-6 space-y-6 overflow-auto">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            >
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Audit-Log</h1>
                        <p className="text-sm text-muted-foreground">
                            Sicherheitsrelevante Aktivitäten im System
                        </p>
                    </div>
                </div>
                <Button onClick={fetchLogs} variant="outline" size="sm" disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Aktualisieren
                </Button>
            </motion.div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{total}</p>
                                <p className="text-xs text-muted-foreground">Einträge gesamt</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                                <LogIn className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">
                                    {logs.filter(l => l.action === 'login').length}
                                </p>
                                <p className="text-xs text-muted-foreground">Logins (Seite)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">
                                    {logs.filter(l => l.action === 'delete').length}
                                </p>
                                <p className="text-xs text-muted-foreground">Löschungen (Seite)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">
                                    {logs.filter(l => l.action === 'login_failed').length}
                                </p>
                                <p className="text-xs text-muted-foreground">Fehlversuche (Seite)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Suchen..."
                                value={filters.search}
                                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                                className="pl-10"
                            />
                        </div>
                        <Select
                            value={filters.entity_type}
                            onValueChange={(value) => {
                                setFilters(f => ({ ...f, entity_type: value === 'all' ? '' : value }));
                                setPage(0);
                            }}
                        >
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Alle Typen" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Typen</SelectItem>
                                <SelectItem value="auth">Authentifizierung</SelectItem>
                                <SelectItem value="user">Benutzer</SelectItem>
                                <SelectItem value="task">Tasks</SelectItem>
                                <SelectItem value="customer">Kunden</SelectItem>
                                <SelectItem value="time_entry">Zeiterfassung</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select
                            value={filters.action}
                            onValueChange={(value) => {
                                setFilters(f => ({ ...f, action: value === 'all' ? '' : value }));
                                setPage(0);
                            }}
                        >
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="Alle Aktionen" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Aktionen</SelectItem>
                                <SelectItem value="login">Login</SelectItem>
                                <SelectItem value="login_failed">Login fehlgeschlagen</SelectItem>
                                <SelectItem value="create">Erstellt</SelectItem>
                                <SelectItem value="update">Bearbeitet</SelectItem>
                                <SelectItem value="delete">Gelöscht</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Log Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                            <p className="text-muted-foreground">Keine Einträge gefunden</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[180px]">Zeitpunkt</TableHead>
                                        <TableHead>Benutzer</TableHead>
                                        <TableHead>Aktion</TableHead>
                                        <TableHead>Bereich</TableHead>
                                        <TableHead className="hidden md:table-cell">Details</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredLogs.map((log, index) => {
                                        const actionStyle = getActionStyle(log.action);
                                        const ActionIcon = actionStyle.icon;
                                        const EntityIcon = getEntityIcon(log.entity_type);
                                        
                                        return (
                                            <motion.tr
                                                key={log.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.02 }}
                                                className="group hover:bg-muted/50"
                                            >
                                                <TableCell className="font-mono text-sm">
                                                    {log.created_at ? (
                                                        <>
                                                            <div>{format(parseISO(log.created_at), 'dd.MM.yyyy', { locale: de })}</div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {format(parseISO(log.created_at), 'HH:mm:ss', { locale: de })}
                                                            </div>
                                                        </>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                                            <User className="w-4 h-4 text-muted-foreground" />
                                                        </div>
                                                        <span className="font-medium">{log.user_name || 'Unbekannt'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary" className={actionStyle.color}>
                                                        <ActionIcon className="w-3 h-3 mr-1" />
                                                        {actionStyle.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <EntityIcon className="w-4 h-4 text-muted-foreground" />
                                                        <span>{getEntityLabel(log.entity_type)}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell max-w-[300px] truncate text-sm text-muted-foreground">
                                                    {formatDetails(log.details)}
                                                </TableCell>
                                            </motion.tr>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        Seite {page + 1} von {totalPages} ({total} Einträge)
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogPage;
